import type { ApiErrorBody, ErrorCode } from '@bs/shared';

/** Structured API error with field-level messages for forms and error summaries. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly fields: Array<{ path: string; message: string }>;
  readonly details: Record<string, unknown>;
  constructor(status: number, body: ApiErrorBody | null) {
    super(body?.error.message ?? `Request failed (${status})`);
    this.status = status;
    this.code = body?.error.code ?? 'internal';
    this.fields = body?.error.fields ?? [];
    this.details = body?.error.details ?? {};
  }
  field(path: string): string | undefined {
    return this.fields.find((f) => f.path === path || f.path.startsWith(`${path}.`))?.message;
  }
}

let csrfToken = '';
export function setCsrfToken(t: string) {
  csrfToken = t;
}
/** For XMLHttpRequest uploads that need progress events. */
export function getCsrfToken(): string {
  return csrfToken;
}

let onUnauthenticated: (() => void) | null = null;
export function setUnauthenticatedHandler(fn: () => void) {
  onUnauthenticated = fn;
}

export async function api<T = unknown>(
  method: string,
  path: string,
  body?: unknown,
  opts: { signal?: AbortSignal } = {},
): Promise<T> {
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: {
      ...(body !== undefined && !isForm ? { 'content-type': 'application/json' } : {}),
      ...(method !== 'GET' && csrfToken ? { 'x-csrf-token': csrfToken } : {}),
    },
    ...(body !== undefined ? { body: isForm ? (body as FormData) : JSON.stringify(body) } : {}),
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!res.ok) {
    const err = new ApiError(res.status, json as ApiErrorBody | null);
    if (res.status === 401 && !path.startsWith('/api/auth/login')) onUnauthenticated?.();
    throw err;
  }
  return json as T;
}

export const get = <T>(path: string, signal?: AbortSignal) =>
  api<T>('GET', path, undefined, signal ? { signal } : {});
export const post = <T>(path: string, body: unknown = {}) => api<T>('POST', path, body);
export const put = <T>(path: string, body: unknown) => api<T>('PUT', path, body);
export const patch = <T>(path: string, body: unknown) => api<T>('PATCH', path, body);
export const del = <T>(path: string, body?: unknown) => api<T>('DELETE', path, body);

export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params))
    if (v !== undefined && v !== null && v !== '') u.set(k, String(v));
  const s = u.toString();
  return s ? `?${s}` : '';
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return 'Something went wrong.';
}
